import { apiClient } from "@/api/client";
import type { AdminAccount, AuthUser, LoginRequest, LoginResponse, RefreshResponse } from "@/types/auth";

/** Talks to apps/auth-service via the API Gateway (see auth.controller.ts). */
export const authApi = {
  /** POST /auth/admin/login — email + password login for admin roles. */
  login: (payload: LoginRequest) =>
    apiClient.post<LoginResponse>("/auth/admin/login", payload).then((r) => r.data),

  /** POST /auth/admin/forgot-password � emails a 6-digit reset code (same response for unknown emails). */
  forgotPassword: (email: string) =>
    apiClient.post<{ ttlSeconds: number }>("/auth/admin/forgot-password", { email }).then((r) => r.data),

  /** POST /auth/admin/reset-password � sets a new password using the emailed code. */
  resetPassword: (payload: { email: string; code: string; newPassword: string }) =>
    apiClient.post<void>("/auth/admin/reset-password", payload).then((r) => r.data),

  /** POST /auth/admin/change-password � revokes every session, so the caller must sign in again. */
  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    apiClient.post<void>("/auth/admin/change-password", payload).then((r) => r.data),

  /** POST /auth/refresh — rotates the refresh token. */
  refresh: (refreshToken: string) =>
    apiClient
      .post<RefreshResponse>("/auth/refresh", { refreshToken })
      .then((r) => r.data),

  /** POST /auth/logout — revokes the current session. */
  logout: (refreshToken?: string | null) =>
    apiClient.post<void>("/auth/logout", { refreshToken }).then((r) => r.data),

  /** GET /auth/me — current principal (used to hydrate the session on app load). */
  me: () => apiClient.get<AuthUser>("/auth/me").then((r) => r.data),

  /** GET /auth/admin/users — every admin account (SUPER_ADMIN only). */
  listAdmins: () => apiClient.get<AdminAccount[]>("/auth/admin/users").then((r) => r.data),

  /**
   * POST /auth/admin/users — create a new admin account (SUPER_ADMIN only).
   * `roleId` is "SUPER_ADMIN", or a CustomRole id from `rolesApi.list()`.
   */
  createAdmin: (payload: { name: string; email: string; password: string; roleId: string }) =>
    apiClient.post<AdminAccount>("/auth/admin/users", payload).then((r) => r.data),

  /** PATCH /auth/admin/users/:id — change another admin's role or active flag (SUPER_ADMIN only). */
  updateAdmin: (id: string, payload: { roleId?: string; active?: boolean }) =>
    apiClient.patch<AdminAccount>(`/auth/admin/users/${id}`, payload).then((r) => r.data),

  /** DELETE /auth/admin/users/:id — permanently remove another admin's account (SUPER_ADMIN only). */
  deleteAdmin: (id: string) => apiClient.delete<void>(`/auth/admin/users/${id}`).then((r) => r.data),
};
